/* eslint-disable react/prop-types */

import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CircularProgress,
  Icon,
  Drawer,
  Popover,
  Box,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import ApplyLeaveForm from "../applyleaveform";
import { EventBusy } from "@mui/icons-material";

import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PregnantWomanIcon from "@mui/icons-material/PregnantWoman";
import WarningIcon from "@mui/icons-material/Warning";
import { ChildFriendly } from "@mui/icons-material";
import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";
import { usersVisibleLeavePolicy } from "api/users";
import FlagLoader from "layouts/flagloader";

export default function LeaveSummaryTab({ userId: propUserId }) {
  const { userPermission } = useAuth();
  const canApplyMyLeave = hasPermission(
    "LeaveTracker.MyData.ApplyButton",
    userPermission
  );

  const userId =
    propUserId || localStorage.getItem("loggedUserId");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [error, setError] = useState(null);

  const [anchorCal, setAnchorCal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const openCal = (e) => setAnchorCal(e.currentTarget);
  const closeCal = () => setAnchorCal(null);
  const changeDate = (d) => {
    setSelectedDate(d);
    closeCal();
  };

  // ICON MAP SAME AS ORIGINAL
  const ICON_MAP = {
    SL: { icon: LocalHospitalIcon, c: "#67C23A", bg: "#c8fab0ff" },
    CL: { icon: BeachAccessIcon, c: "#2196f3", bg: "#c0e2feff" },
    PL: { icon: ChildFriendly, c: "#ec407a", bg: "#fdbed3ff" },
    ML: { icon: PregnantWomanIcon, c: "#ffa726", bg: "#f9e0bbff" },
    NL: { icon: WarningIcon, c: "#9B59B6", bg: "#f2d1ffff" },
  };

  const getIcon = (code) => {
    const cfg = ICON_MAP[code] || ICON_MAP["NL"];
    const Comp = cfg.icon;
    return (
      <Comp
        sx={{
          color: cfg.c,
          fontSize: 20,
          padding: "10px",
          backgroundColor: cfg.bg,
          height: 40,
          width: 40,
          borderRadius: "8px",
        }}
      />
    );
  };

  // Fetch API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await usersVisibleLeavePolicy(userId);
      let arr = [];

      if (Array.isArray(res?.data)) arr = res.data;
      else if (Array.isArray(res?.data?.policies)) arr = res.data.policies;
      else if (Array.isArray(res)) arr = res;

      const mapped = arr.map((leave) => ({
        id: leave.id,
        leave_name: leave.leave_name,
        leave_code: leave.leave_code,
        available: leave.stats?.available || 0,
        booked: leave.stats?.booked || 0,
      }));

      setLeaveTypes(mapped);
      setError(null);
    } catch (err) {
      console.log("Leave summary error:", err);
      setLeaveTypes([]);
      setError("Failed to load leave policies");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchData();
  }, [userId]);

  useEffect(() => {
    if (!drawerOpen) fetchData();
  }, [drawerOpen]);

  return (
    <SoftBox>
      <Card>
        {/* Top Right */}
        <Box mt={1} display="flex" alignItems="center" justifyContent="right">
          <SoftBox mr={2}>
            {canApplyMyLeave && (
              <SoftButton
                color="info"
                variant="gradient"
                startIcon={<Icon>add</Icon>}
                onClick={() => setDrawerOpen(true)}   // ya jo bhi handler ho
              >
                Apply Leave
              </SoftButton>
            )}
          </SoftBox>
        </Box>

        {/* Loading */}
        {loading ? (
          <SoftBox
            p={3}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            {/* <CircularProgress size={40} thickness={4} /> */}
            <FlagLoader />
          </SoftBox>
        ) : (
          <SoftBox
            mt={2}
            display="flex"
            flexWrap="wrap"
            justifyContent={leaveTypes.length <= 3 ? "flex-start" : "space-between"}
            gap={2}
          >
            {leaveTypes.length > 0 ? (
              leaveTypes.map((leave) => (
                <Card
                  key={leave.id}
                  style={{
                    padding: 16,
                    textAlign: "center",
                    backgroundColor: "white",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    minWidth: "180px",
                    flex: "1 1 0px",
                    margin: "8px",
                  }}
                  onClick={() => {
                    setSelectedLeaveType(leave.leave_name);
                    setDrawerOpen(true);
                  }}
                >
                  <SoftBox display="flex" justifyContent="center" alignItems="center" mb={1}>
                    {getIcon(leave.leave_code)}
                  </SoftBox>

                  <SoftTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {leave.leave_name}
                  </SoftTypography>

                  <SoftBox display="flex" justifyContent="space-between" mb={1}>
                    <SoftTypography variant="body2">Available</SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      {leave.available}
                    </SoftTypography>
                  </SoftBox>

                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftTypography variant="body2">Booked</SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      {leave.booked}
                    </SoftTypography>
                  </SoftBox>
                </Card>
              ))
            ) : (
              <SoftBox
                width="100%"
                textAlign="center"
                py={6}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <EventBusy sx={{ fontSize: 64, color: "text.secondary" }} />
                <SoftTypography variant="h6" fontWeight="medium" color="text">
                  No Leave Policies Found
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        )}

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          hideBackdrop
          PaperProps={{
            sx: {
              width: "90vw",
              maxWidth: "90vw",
            },
          }}
        >
          <SoftBox p={3} display="flex" gap={3}>
            <ApplyLeaveForm
              selectedLeaveType={selectedLeaveType}
              handleClose={() => setDrawerOpen(false)}
            />
          </SoftBox>
        </Drawer>
      </Card>
    </SoftBox>
  );
}
